import { useMemo, useState } from 'react'
import { Check, ChevronDown, SlidersHorizontal } from 'lucide-react'
import { ExchangeBadge } from '@/components/common/ExchangeBadge'
import { cn } from '@/lib/utils'

interface OrderbookSourcePickerProps {
  sources: Array<{ id: string; exchange: string; market: string }>
  selectedSourceIds: string[]
  onToggle: (sourceId: string) => void
}

export function OrderbookSourcePicker({ sources, selectedSourceIds, onToggle }: OrderbookSourcePickerProps) {
  const [expanded, setExpanded] = useState(true)
  const selected = useMemo(() => new Set(selectedSourceIds.map((id) => id.toLowerCase())), [selectedSourceIds])
  const { exchanges, markets } = useMemo(() => {
    const rows = new Map<string, Map<string, string>>()
    const columns = new Set(['spot', 'futures'])
    for (const source of sources) {
      const exchange = source.exchange.toLowerCase()
      const market = source.market.toLowerCase()
      if (!rows.has(exchange)) rows.set(exchange, new Map())
      rows.get(exchange)!.set(market, source.id)
      columns.add(market)
    }
    return { exchanges: Array.from(rows), markets: Array.from(columns) }
  }, [sources])
  const columns = { gridTemplateColumns: `minmax(100px, 1fr) repeat(${markets.length}, 64px)` }

  return (
    <section aria-label="Orderbook sources" className="shrink-0 border-b border-border/30 bg-muted/10">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        aria-label="Choose orderbook sources"
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left outline-none transition-colors hover:bg-muted/30 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-400"
      >
        <SlidersHorizontal className="h-3.5 w-3.5 text-teal-400" />
        <span className="flex-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Exchanges & markets</span>
        <span className="text-[10px] tabular-nums text-teal-400">{selected.size} selected</span>
        <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', expanded && 'rotate-180')} />
      </button>
      {expanded && (
        <div className="px-2 pb-2">
          <div className="max-h-[min(240px,32vh)] overflow-auto rounded-lg border border-border/40 bg-background/50 custom-scrollbar">
            <div className="min-w-max">
              <div className="sticky top-0 z-10 grid items-center gap-1 border-b border-border/40 bg-background px-2 py-1.5" style={columns}>
                <span className="text-[9px] uppercase tracking-wide text-muted-foreground/70">Exchange</span>
                {markets.map((market) => <span key={market} className="text-center text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{market}</span>)}
              </div>
              {exchanges.map(([exchange, sourceIds]) => (
                <div key={exchange} className="grid items-center gap-1 border-b border-border/20 px-2 py-1 last:border-0 hover:bg-muted/20" style={columns}>
                  <div className="min-w-0 pr-1"><ExchangeBadge exchange={exchange} size="sm" className="max-w-full truncate" /></div>
                  {markets.map((market) => {
                    const sourceId = sourceIds.get(market)
                    if (!sourceId) return <span key={market} className="text-center text-xs text-muted-foreground/30" aria-label={`${exchange} ${market} is not configured`}>—</span>
                    const checked = selected.has(sourceId.toLowerCase())
                    return (
                      <label key={market} title={`${checked ? 'Hide' : 'Show'} ${exchange} ${market} orderbook`} className="relative block cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggle(sourceId)}
                          aria-label={`${exchange} ${market} orderbook`}
                          className="peer sr-only"
                        />
                        <span className={cn(
                          'flex h-7 items-center justify-center rounded-md border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-teal-400 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-background',
                          checked ? 'border-teal-400/40 bg-teal-400/15 text-teal-300 hover:bg-teal-400/25' : 'border-border/50 bg-muted/10 text-muted-foreground/40 hover:border-teal-400/40 hover:bg-teal-400/5',
                        )}>
                          <span className={cn('flex h-3.5 w-3.5 items-center justify-center rounded border', checked ? 'border-teal-400 bg-teal-400 text-slate-950' : 'border-current')}>
                            {checked && <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />}
                          </span>
                        </span>
                      </label>
                    )
                  })}
                </div>
              ))}
              {exchanges.length === 0 && <p className="px-3 py-4 text-center text-xs text-muted-foreground">No orderbook sources configured.</p>}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
